import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { useFormContext, useWatch } from "react-hook-form";
import { Button } from "@mui/material";
import { PersistentDrawer } from "@/app/ui/PersistentDrawer";
import type { BookingData } from "@/app/ui/trips/types";
import { TravellerSelectionStatusDrawer } from "@/app/ui/travellers/TravellerSelectionStatusDrawer";
import { SwipeableDrawer } from "@/app/ui/SwipeableDrawer";
import { TravellersSelection } from "@/app/ui/travellers/TravellersSelection";
import { DrawerTopBar } from "@/app/ui/Drawer";
import TravellersList from "@/app/ui/travellers/TravellersList";
import type { TravellerAgeGroup } from "@/app/lib/types/types";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";
import { travelerTypeToTypeCode } from "@/app/lib/utils/transformTraveler";
import { getTravellerTypeAtTravelDate } from "@/app/lib/utils/ageValidation";
import {
  TravellersSelectionProvider,
  useTravellersSelectionContext,
} from "@/app/lib/context/TravellersSelectionContext";

export const Route = createFileRoute(
  "/_authenticated/trips/$tripId/travellers",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { getValues } = useFormContext<BookingData>();
  const tripDateString = getValues("tripDate");
  const selectedTravellers = getValues("selectedTravellers");

  return (
    <TravellersSelectionProvider
      initialTravellersSlots={selectedTravellers}
      travelDate={new Date(tripDateString)}
    >
      <RouteComponentInner />
    </TravellersSelectionProvider>
  );
}

function RouteComponentInner() {
  const t = useTranslations();
  const navigate = Route.useNavigate();
  const { data: travellersData } = useQuery(travellersQueryOptions());

  const { setValue } = useFormContext<BookingData>();
  const tripDateString = useWatch<BookingData, "tripDate">({
    name: "tripDate",
  });
  const tripDate = new Date(tripDateString);
  const { travellerSlots, selectedTravellers, selectTraveller } =
    useTravellersSelectionContext();
  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  return (
    <>
      <h2 className="text-xl font-medium text-text-title">
        {t("trips.travellersPage.title")}
      </h2>
      <p className="mb-4 text-sm text-text-tertiary">
        {t("trips.travellersPage.subtitle")}
      </p>

      <TravellersSelection
        onSlotClick={(ageGroup, index) => {
          setSelectedSlot({ ageGroup, index });
        }}
      />

      <SwipeableDrawer
        hasFullHeight
        heightOffset={24}
        open={!!selectedSlot}
        onClose={() => setSelectedSlot(null)}
        backgroundColor="var(--color-surface-background)"
      >
        <DrawerTopBar
          onCloseClick={() => setSelectedSlot(null)}
          title={
            selectedSlot
              ? t("travellers.addFor", {
                  type: t("travellers.travellerTypeN", {
                    type: selectedSlot.ageGroup,
                    number: selectedSlot.index + 1,
                  }),
                })
              : null
          }
        />
        {selectedSlot ? (
          <TravellersList
            travellers={(travellersData || []).filter(
              (traveller) =>
                travelerTypeToTypeCode(
                  getTravellerTypeAtTravelDate(traveller.birthDate, tripDate),
                ) === selectedSlot.ageGroup,
            )}
            travelDate={tripDate}
            selectedTravellerId={
              travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
            }
            isSelectionDisabled={(traveller) =>
              selectedTravellers.some((t) => t.id === traveller.id)
            }
            onTravellerSelected={(travellerId) => {
              selectTraveller(selectedSlot.index, travellerId);
              setSelectedSlot(null);
            }}
          />
        ) : null}
      </SwipeableDrawer>

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <PersistentDrawer>
        <Button
          fullWidth
          variant="contained"
          disabled={!allTravellersSelected}
          onClick={() => {
            setValue("selectedTravellers", travellerSlots);
            navigate({ to: "../summary" });
          }}
        >
          {t("trips.continue")}
        </Button>
      </PersistentDrawer>
    </>
  );
}
